import { useCallback, useEffect, useMemo, useState } from "react";
import { IMenuOption } from "../../../types/global";
import { useTranslation } from "react-i18next";
import { useStateContext } from "../../../contexts/auth-context";

export const useSalesReturnOption = (status?: string | null) => {
  const { t } = useTranslation();
  const {
    state: { permissions },
  } = useStateContext();

  const defaultOptions: IMenuOption[] = useMemo(
    () => [
      {
        value: "แก้ไข",
        disabled: false,
      },
      {
        value: "ยกเลิก",
        disabled: false,
      },
    ],
    []
  );

  const editOption = defaultOptions[0];
  const cancelOption = defaultOptions[1];

  const canUpdate = useCallback(() => {
    return permissions?.sales_return?.update;
  }, [permissions]);

  const canCancel = useCallback(() => {
    return permissions?.sales_return?.cancel;
  }, [permissions]);

  const externalRefOption: IMenuOption = useMemo(
    () => ({ value: t("sales.external_reference_no"), disabled: false }),
    [t]
  );

  // Helper function to get options based on permissions and status
  const getOptionsForStatus = useCallback(
    (status?: string | null) => {
      if (["draft", "not_approved"].includes(status || "")) {
        return canCancel() ? [cancelOption] : [];
      }

      if (status === "cancelled") {
        return [];
      }

      if (status === "approved") {
        if (canUpdate() && canCancel()) {
          return [externalRefOption, ...defaultOptions];
        }
        if (canUpdate() && !canCancel()) {
          return [editOption];
        }
        if (canCancel()) {
          return [cancelOption];
        }
        return [];
      }

      if (status === "finished") {
        if (canUpdate()) {
          return [externalRefOption];
        }
        return [];
      }

      // Default case when no specific status matches
      return canUpdate() && canCancel()
        ? defaultOptions
        : canUpdate()
        ? [editOption]
        : canCancel()
        ? [cancelOption]
        : [];
    },
    [
      canCancel,
      canUpdate,
      cancelOption,
      defaultOptions,
      editOption,
      externalRefOption,
    ]
  );

  const [selectModifyOptions, setSelectedModifyOptions] =
    useState<IMenuOption[]>(defaultOptions);

  useEffect(() => {
    const options = getOptionsForStatus(status);
    setSelectedModifyOptions(options);
  }, [status, getOptionsForStatus]);

  return selectModifyOptions;
};
